import type {SyntheticEvent} from 'react';
import type {EventHashedID, EventVO} from '@Components/events/events.types';
import type {LoadingStates} from '@Utils/loading.util';
import type {SocialPost} from '@Components/social-media/post.vo';
import type {EmailCampaign, EmailCampaignId} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import type {PromotionMetricType} from '@Components/mystuff-event-promotion-page/components/promotion-metrics-row/promotion-metrics-row.types';
import {PerformanceDrawerTab} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/promotion-performance-drawer.types';
import type {RecentAction} from '@Components/mystuff-event-promotion-page/components/promotion-performance-drawer/components/performance-reach-tab/performance-reach-tab.types';

export type PromotionSteps = PromotionStep[];

export interface MystuffEventPromotionPageProps {}

export enum PromotionType {
  EVENT_PAGE = 'event-page',
  SHARE_EVENT_PAGE_LINK = 'share-event-page-link',
  SOCIAL_POSTS = 'social-posts',
  EMAIL_CAMPAIGNS = 'email-campaigns',
  EMBED_IN_WEBPAGE = 'embed-in-webpage',
  PRINT_FLYERS = 'print-flyers',
  SETUP_CALENDAR = 'setup-calendar',
  PROMOTE_CALENDAR = 'promote-calendar',
}

export interface EventPromotionStateBase {
  event: EventVO;
  stats: EventStats;
  promotionSteps: PromotionSteps;
  socialPosts: SocialPost[];
  emailCampaigns: EmailCampaign[];
  printFlyers: PrintFlyerPoster[];
  areEventPreviewsReady: boolean;
  isCalendarSetUp: boolean;
  calendarPageUrl: string | null;
  /** Server-driven: whether this viewer may see the event's ticketing money (Premium and allowed to modify it). */
  canViewEarnings: boolean;
}

export interface EventPromotionReducerState extends EventPromotionStateBase {
  loadingState: LoadingStates;
  hasFetchEventFailed: boolean;
  mobileViewActivePromotionType?: PromotionType;
  /** The page's own narrow (<=600) arrangement, which the top bar reads to place the progress pill. */
  isPageNarrow: boolean;
}

export type FetchEventPromotionResponse = EventPromotionStateBase;

/** The Performance drawer tab each metric's card opens; a metric with no tab has nothing to open. */
export const PROMOTION_METRIC_DRAWER_TABS: Partial<Record<PromotionMetricType, PerformanceDrawerTab>> = {
  reach: PerformanceDrawerTab.REACH,
  earnings: PerformanceDrawerTab.EARNINGS,
};

export interface EventStatSeries {
  /** Latest figure for the metric. */
  value: number;
  /** Seven daily points, oldest first. Empty when the event has no history yet. */
  series: number[];
  /** Signed change versus the previous 7 days. Null when there is no comparison period. */
  percentageChange: number | null;
  /** ISO currency code, for a metric whose value is money (earnings). Null for a count metric — the key is
   * always on the wire (EventStatSeries::jsonSerialize emits it unconditionally). */
  currency: string | null;
}

export interface EventStats {
  views: number;
  /** Views of the event's card embedded on other sites; already part of `reach`. */
  embedViews: number;
  registrations: number;
  reach: number;
  /** Emails delivered across the event's linked campaigns; already part of `reach`. */
  emailReach: number;
  reachSeries: EventStatSeries;
  earningsSeries: EventStatSeries;
  registrationsSeries: EventStatSeries;
  viewsSeries: EventStatSeries;
  /** Null when the viewer may not see earnings or the event has no ticketing stats. */
  feeSplit: EventTicketingFeeSplit | null;
}

/** How many items the Reach tab's Recent actions feed shows. */
export const MAX_RECENT_ACTIONS = 5;

/** A Recent actions item with the unix time (seconds) the feed is ordered by. */
export interface TimedRecentAction {
  timestampInSeconds: number;
  recentAction: RecentAction;
}

/** PMW's cut and the payment-processing recovery on the event's ticket sales, in the earnings currency. */
export interface EventTicketingFeeSplit {
  platformFee: number;
  processingFee: number;
  /** The processing rate charged once per card order: a percent plus a fixed USD amount. */
  processingFeePercent: number;
  processingFeeFixed: number;
}

export interface PrintFlyerPoster {
  posterId: number;
  posterHashedID: string;
  previewImageURL: string;
  isVideo: boolean;
  posterName: string;
  isPreviewOutdated: boolean;
  idcreator: number;
  previewVideoURL?: string;
  isLoading?: boolean;
}

export interface EventPromotionMetrics {
  numPageViews: number;
  totalReach: number;
  numRegistrations: number;
}

export interface PromotionStep {
  type: PromotionType;
  isCompleted: boolean;
  isSkipped: boolean;
  sortOrder: number;
  /** Server-driven: whether this step counts toward "N of 4". Deprecated and "Other ways" steps are false. */
  isCounted: boolean;
}

export interface PromotionStepAjaxParams {
  promotionType: PromotionType;
  eventHashedID: EventHashedID;
}

export interface DeleteSocialPostFromEventAjaxParams {
  eventHashedID: EventHashedID;
  socialPostId: string;
}

export interface EventEmailCampaignAjaxParams {
  eventHashedID: EventHashedID;
  emailCampaignId: EmailCampaignId;
}

export interface DeletePosterFromEventAjaxParams {
  eventHashedID: EventHashedID;
  posterHashedId: string;
}

export type EventPromotionTypeCallback = (promotionType: PromotionType) => void;


export const EMBED_EVENT_HTML_URI = 'event/getEmbedEventHtml';

/**
 * The event is optional because the same handler is reached from two places: a checklist row that hands over its
 * click event (the print-flyers row stops propagation with it) and the pill popover, which reports a step type only.
 */
export type PromotionStepClickHandler = (event?: SyntheticEvent) => void;

export interface PromotionStepClickHandlers {
  getClickHandlerForPromotionType: (promotionType: PromotionType) => PromotionStepClickHandler | undefined;
  doShowCopyLinkFeedback: boolean;
}

export interface PromoteCalendarChatLauncher {
  /** True while the chat is being created, so the CTA shows its spinner and ignores further clicks. */
  isCreatingChat: boolean;
  onPromote: VoidFunction;
}

